import {
  CameraManager,
  MicrophoneIcon,
  Tooltip,
  useCameraStore,
  useIdentityContext,
  VideoIcon,
} from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InactiveSessionView from '../components/InactiveSessionView';
import MicrophoneRing from '../components/MicrophoneRing';
import NavBar from '../components/NavBar';
import MicrophoneManager from '../managers/MicrophoneManager';
import { useMicrophoneStore } from '../stores/microphoneStore';
import iconPng from '/icons/icon.png';

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useIdentityContext();
  const { provideTailwind } = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [isJoiningSession, setIsJoiningSession] = useState(false);

  // Media controls
  const videoRef = useRef<HTMLVideoElement>(null);
  const { cameraStream, isVideoOn, setIsVideoOn } = useCameraStore();
  const { isMicOn, setIsMicOn } = useMicrophoneStore();

  const apiClient = useReactQuery();

  // Redirect to signin if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      const currentPath = location.pathname;
      navigate(`/signin?referrer=${encodeURIComponent(currentPath)}`);
    }
  }, [isLoggedIn, navigate, location.pathname]);

  // Fetch session details
  const fetchSession = apiClient.useQuery(
    'get',
    `/v1/sessions/{session_id}`,
    {
      params: {
        path: {
          session_id: sessionId ?? '',
        },
      },
    },
    {
      enabled: isLoggedIn && !!sessionId,
    },
  );

  // Update video element when camera stream changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // Handle session data
  const handleSessionSuccess = useCallback(
    (session: any) => {
      if (isLoggedIn) {
        setSessionData(session);
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    if (fetchSession.data?.item) {
      handleSessionSuccess(fetchSession.data.item);
    }
  }, [fetchSession.data, handleSessionSuccess]);

  useEffect(() => {
    if (fetchSession.error) {
      console.error('Failed to fetch session:', fetchSession.error);
      setError('Session not found or access denied.');
    }
  }, [fetchSession.error]);

  const toggleVideo = () => {
    setIsVideoOn(!isVideoOn);
  };

  const toggleAudio = () => {
    setIsMicOn(!isMicOn);
  };

  const handleJoinSession = () => {
    if (sessionData?.id) {
      setIsJoiningSession(true);
      // Navigate to the active session page which will handle the LiveKit connection
      navigate(`/sessions/${sessionData.id}/active`);
    }
  };

  // Show loading state
  if (fetchSession.isLoading) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className={`${provideTailwind('text-secondary')}`}>
              Loading session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || fetchSession.error) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1
              className={`text-2xl font-bold mb-4 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Session Not Found
            </h1>
            <p className={`mb-6 ${provideTailwind('text-secondary')}`}>
              {error ||
                'This session could not be found or you do not have access to it.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If session is active, show the lobby interface
  if (sessionData?.state === 'active') {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        {/* Media Managers */}
        <CameraManager isVideoOn={isVideoOn} />
        <MicrophoneManager isMicOn={isMicOn} />

        <NavBar />

        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className={`text-4xl font-bold mb-4 ${provideTailwind(
                'text-primary',
              )}`}
            >
              {sessionData?.title || 'Untitled Session'}
            </h1>
            <p className={`text-xl ${provideTailwind('text-secondary')}`}>
              Test your camera and microphone before joining
            </p>
          </div>

          {/* Interactive Circle - Similar to InteractiveSpotlight */}
          <div className="relative mb-12">
            {/* Main circle with video/logo */}
            <div className="w-80 h-80 rounded-full bg-black flex items-center justify-center relative overflow-hidden shadow-2xl">
              {/* Microphone Ring */}
              <MicrophoneRing size={320} strokeWidth={8} />
              {/* Video feed when video is on */}
              {isVideoOn && cameraStream && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover rounded-full"
                />
              )}

              {/* Flik logo in the center - only show when video is off */}
              {!isVideoOn && (
                <div className="relative z-10 flex items-center justify-center">
                  <img
                    src={iconPng}
                    alt="Flik"
                    width="200"
                    height="200"
                    className="text-white"
                  />
                </div>
              )}
            </div>

            {/* Microphone indicator - positioned inside the circle */}
            <div
              className="absolute"
              style={{
                zIndex: 999,
                bottom: 'calc(50% - 120px)',
                left: 'calc(50% - 60px)',
              }}
            >
              <Tooltip
                content={isMicOn ? 'Turn off microphone' : 'Turn on microphone'}
                direction="down"
                className="!left-1/2 !transform !-translate-x-1/2"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-110 select-none ${
                    isMicOn ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  onClick={toggleAudio}
                >
                  <MicrophoneIcon
                    width={16}
                    height={16}
                    className="text-white"
                  />
                </div>
              </Tooltip>
            </div>

            {/* Video indicator - positioned inside the circle */}
            <div
              className="absolute"
              style={{
                zIndex: 999,
                bottom: 'calc(50% - 120px)',
                right: 'calc(50% - 60px)',
              }}
            >
              <Tooltip
                content={isVideoOn ? 'Turn off video' : 'Turn on video'}
                direction="down"
                className="!left-1/2 !transform !-translate-x-1/2"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-110 select-none ${
                    isVideoOn ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  onClick={toggleVideo}
                >
                  <VideoIcon width={16} height={16} className="text-white" />
                </div>
              </Tooltip>
            </div>
          </div>

          {/* Join Button */}
          <div className="text-center">
            <button
              onClick={handleJoinSession}
              disabled={!sessionData || isJoiningSession}
              className="text-black px-8 py-3 rounded-full text-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm relative"
              style={{
                background:
                  !sessionData || isJoiningSession
                    ? 'linear-gradient(#f3f4f6, #f3f4f6) padding-box, linear-gradient(to right, #d1d5db, #d1d5db) border-box'
                    : 'linear-gradient(white, white) padding-box, linear-gradient(to right, #ec4899, #8b5cf6) border-box',
                border: '1px solid transparent',
              }}
            >
              {isJoiningSession ? 'Joining...' : 'Join Flik'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If session is inactive, show the new inactive session view
  if (sessionData?.state === 'inactive') {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />
        <InactiveSessionView
          sessionTitle={sessionData?.title}
          sessionId={sessionData?.id}
        />
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <NavBar />
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className={`${provideTailwind('text-secondary')}`}>
            Loading session...
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionPage;
